import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, ProgressBar } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { register, login } from '../services/api';

const allowSelfRegistration = process.env.REACT_APP_ALLOW_SELF_REGISTRATION === 'true';
const requiresInvitation = !allowSelfRegistration;

function Register({ onLogin }) {
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState('');

  // Autofill invitation token from query string (?invite=token)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const inviteParam = searchParams.get('invite');
    if (inviteParam) {
      setInvitationToken(inviteParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (invitationToken) {
      localStorage.setItem('pendingInviteToken', invitationToken);
    } else {
      localStorage.removeItem('pendingInviteToken');
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('pending-invite-updated'));
    }
  }, [invitationToken]);

  // Password strength calculation
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback('');
      return;
    }

    // Criteria for password strength
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChars = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    // Calculate strength score (0-100)
    let strength = 0;
    if (hasMinLength) strength += 20;
    if (hasUppercase) strength += 20;
    if (hasLowercase) strength += 20;
    if (hasNumbers) strength += 20;
    if (hasSpecialChars) strength += 20;

    setPasswordStrength(strength);

    // Provide feedback
    if (strength < 40) {
      setPasswordFeedback('Weak password');
    } else if (strength < 80) {
      setPasswordFeedback('Moderate password');
    } else {
      setPasswordFeedback('Strong password');
    }
  }, [password]);

  // Get progress bar variant based on strength
  const getProgressBarVariant = () => {
    if (passwordStrength < 40) return 'danger';
    if (passwordStrength < 80) return 'warning';
    return 'success';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!username || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (requiresInvitation && !invitationToken) {
      setError('Invitation code is required. Please paste the code from your administrator.');
      return;
    }

    // Password strength validation
    if (passwordStrength < 60) {
      setError('Please use a stronger password. Include uppercase, lowercase, numbers, and special characters.');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      // Register the user
      await register(username, email, password, invitationToken);
      
      // Automatically login after successful registration
      const loginResponse = await login(username, password);

      localStorage.removeItem('pendingInviteToken');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('pending-invite-updated'));
      }
      // onLogin (App.js's handleLogin) expects the full response payload as a
      // single object (it reads userData.token/user_id/username/is_admin) --
      // passing separate positional args here left userData.token undefined,
      // silently no-opping the entire post-registration login.
      onLogin(loginResponse.data);
    } catch (err) {
      console.error('Registration error:', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to register. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center">
      <Card style={{ width: '400px' }}>
        <Card.Body>
          <Card.Title className="text-center mb-4">Register</Card.Title>
          
          {error && <Alert variant="danger">{error}</Alert>}
          {requiresInvitation && (
            <Alert variant="info">
              Access requires an invitation code. Contact an administrator to receive a link or code, then paste it below.
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formInvitation">
              <Form.Label>Invitation Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter invitation code"
                value={invitationToken}
                onChange={(e) => setInvitationToken(e.target.value)}
                required={requiresInvitation}
              />
              <Form.Text className="text-muted">
                Paste the code or share link token provided by your administrator.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {password && (
                <>
                  <ProgressBar 
                    className="mt-2" 
                    now={passwordStrength} 
                    variant={getProgressBarVariant()} 
                  />
                  <Form.Text className={`text-${getProgressBarVariant()}`}>
                    {passwordFeedback}
                  </Form.Text>
                  <Form.Text className="text-muted mt-2">
                    Password must be at least 8 characters long and include uppercase, lowercase, 
                    numbers, and special characters.
                  </Form.Text>
                </>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="formConfirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Form.Group>
            
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
              >
                {loading ? 'Registering...' : 'Register'}
              </Button>
            </div>
          </Form>
          
          <div className="text-center mt-3">
            <p>
              Already have an account? <Link to="/login">Login</Link>
            </p>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default Register; 
